import React from 'react';
import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import { EventStatus, ICreateEventDTO, IEventDetailsDTO, IEventsDAO } from '../../../../api/events.dao';
import { IErrorParsing } from '../../../../infrastructure/error-parsing';
import { alertServerError, alertServerWarning } from '../../../global/modal/alerts-host/alert-templates';
import { IAlertsService } from '../../../../infrastructure/alerts';
import { EventFormDataVM } from './event-form-data.viewmodel';
import { JobDtoType } from '../../../../domain/jobs';
import { JobTrackingService } from '../../../../domain/jobs/implementations/job-tracking.service';
import TrackingServiceProvider from '../../../../tracking-service/tracking-service-provider';
import { convertBoolTypeForMixPanel, SALESFORCE_ORDER_TRACKING_EVENTS, TRACKING_EVENTS } from '../../../../tracking-service/tracking-values';
import { SalesforceSharedFieldsTrackingMapper } from '../../_manage-event/shared/salesforce-order/salesforce-shared-fields-tracking.mapper';
import { ORDER_SAVE_WARNING_TITLE } from '../../_manage-event/shared/salesforce-order/salesforce-order.texts';
import { Space } from '../../../../constants/html-character-entities';
import { IGlobalStorage } from '../../../../infrastructure/global-storage';
import { TObject } from '../../../../infrastructure/type-extensions';
import { NONE } from '../../../../constants/common.constants';
import { errorText } from '../../../../constants/common-labels.constants';
import { IReactIntlProvider } from '../../../../infrastructure/react-intl-provider';
import {
    ARE_YOU_SURE_YOU_WANT_TO_TEXT,
    CANCEL_EVENT_TEXT,
    CANCEL_THIS_EVENT_TEXT,
    DELETE_EVENT_TEXT,
    DELETE_THIS_EVENT_TEXT,
    NO_GO_BACK_TEXT,
    OK_TEXT,
    REINSTATE_EVENT_TEXT,
    REINSTATE_THIS_EVENT_TEXT,
    SALESFORCE_SYNC_FAILED_TITLE,
    SETTING_UP_BLUEPRINT_ALERT_TEXT
} from '../../events.message-descriptors';
import { transpileInlineStylesToClasses } from '../../../global/rich-text-editor/utils/transpiler';
import { IAttributesDAO, IEventAttributeDetailsDTO, TGetEventAttributesSortKeys } from '../../../../api/attributes.dao';
import { ISortRequestParams, SortOrder } from '../../../../api/shared.dto';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../api/roles.dao';
import { ISalesforceOrderDAO } from '../../../../api/salesforce-order.dao';

enum ERRORS {
    ORDER_ID_NO_UNIQUE = 'UC_EVENTSORDER_ID_COL',
    EXTERNAL_ID_NO_UNIQUE = 'UC_EVENTSEXTERNAL_ID_COL'
}
class EventFormVM {
    @mobx.observable
    private saving: boolean;

    @mobx.observable
    public formFields: EventFormDataVM;

    @mobx.observable
    private eventAttributes: IEventAttributeDetailsDTO[] = [];

    @mobx.observable
    private loading: boolean;

    private cachedEventObject: IEventDetailsDTO;

    constructor(
        private readonly accountId: number,
        private readonly eventId: number,
        private readonly eventSavedCallback: (eventId?: number) => void,
        private readonly eventDeleteCallback: () => void,
        private readonly eventsDAO: IEventsDAO = IoC.Container.get(IEventsDAO),
        private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        private readonly alertsService : IAlertsService = IoC.Container.get(IAlertsService),
        private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        private readonly jobTrackingService: JobTrackingService = IoC.Container.get(JobTrackingService),
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage),
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
        @IoC.Inject private readonly attributesDAO: IAttributesDAO = IoC.Container.get(IAttributesDAO),
        @IoC.Inject private readonly salesforceOrderDAO: ISalesforceOrderDAO = IoC.Container.get(ISalesforceOrderDAO),
    ) {}

    public get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }


    public getFormFields() {
        return this.formFields;
    }

    public isLoading() {
        return this.loading;
    }

    public isSaving() {
        return this.saving;
    }

    public getEventId() {
        return this.eventId;
    }
    public getAccountId(): number {
        return this.accountId;
    }

    public getEventAttributes() {
        return this.eventAttributes;
    }

    @mobx.action
    public initEventInfo = async (): Promise<void> => {
        // re-fetch event information only if starts working on another event
        if (this.eventId && (!this.cachedEventObject || this.cachedEventObject.eventId !== this.eventId)) {
            this.loading = true;
            if (hasPermission(Permission.VIEW_EVENT_ATTRIBUTES)) {
                await this.loadEventAttributes();
            }
            await this.completeEventFetch(await this.eventsDAO.getEventDetails({
                eventId: this.eventId,
                accountId: this.accountId,
            }));
        } else {
            if (hasPermission(Permission.VIEW_EVENT_ATTRIBUTES)) {
                await this.loadEventAttributes();
            }
            this.formFields = new EventFormDataVM(this.accountId);
            this.formFields.setEventAttributes(this.eventAttributes);
        }
    };

    @mobx.action
    public async loadEventAttributes(): Promise<void> {
        try {
            const requestParams: { accountId: number; deliveryTeamMembers?: string; coordinators?: string }
                & ISortRequestParams<TGetEventAttributesSortKeys> = {
                accountId: this.accountId,
                sortField: 'createdAt',
                sortOrder: SortOrder.Desc
            };
            const response = await this.attributesDAO.getEventAttributesList(requestParams);
            mobx.runInAction(() => {
                this.eventAttributes = response.content.filter(item => item.active);
                if (this.formFields) {
                    this.formFields.setEventAttributes(this.eventAttributes);
                }
            });
        } catch (error) {
            alertServerError(errorText(), this.errorParsingService.extractMessage(error));
        }
    }

    public deleteEvent = (): boolean => {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    {this.intl.formatMessage(ARE_YOU_SURE_YOU_WANT_TO_TEXT)}
                    <span className='alert-message--danger'> {this.intl.formatMessage(DELETE_THIS_EVENT_TEXT)}</span>?
                </span>
            ),
            confirmButtonProps: {
                text: this.intl.formatMessage(DELETE_EVENT_TEXT),
                modifier: 'confirm-warn',
                handler: this.deleteEventApiCall,
            },
            cancelButtonProps: {
                modifier: 'cancel'
            }
        });
        return false;
    };

    public deleteEventApiCall = async (): Promise<void> => {
        try {
            const response = await this.eventsDAO.deleteEvent({
                accountId: this.accountId,
                eventId: this.eventId,
            });
            this.trackTriggerEventDeletion(response);
            await this.eventDeleteCallback();
        } catch (error) {
            alertServerError(errorText(), this.errorParsingService.extractMessage(error));
        }
    };

    public cancelEvent = (): boolean => {
        const isEventCancelled = this.formFields.eventStatus === EventStatus.CANCELLED;
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    {this.intl.formatMessage(ARE_YOU_SURE_YOU_WANT_TO_TEXT)}
                    <Space />
                    <span className='alert-message--danger'>
                       {isEventCancelled ? this.intl.formatMessage(REINSTATE_THIS_EVENT_TEXT) : this.intl.formatMessage(CANCEL_THIS_EVENT_TEXT)}
                    </span>
                    ?
                </span>
            ),
            confirmButtonProps: {
                text: isEventCancelled ? this.intl.formatMessage(REINSTATE_EVENT_TEXT) : this.intl.formatMessage(CANCEL_EVENT_TEXT),
                modifier: 'confirm-warn',
                handler: this.cancelEventApiCall
            },
            cancelButtonProps: {
                text: this.intl.formatMessage(NO_GO_BACK_TEXT),
                modifier: 'cancel'
            }
        });
        return false;
    };

    @mobx.action
    public cancelEventApiCall = async (): Promise<void> => {
        try {
            const isEventCancelled = this.formFields.eventStatus === EventStatus.CANCELLED;
            this.formFields.setEventStatus(isEventCancelled ? EventStatus.IN_PROGRESS : EventStatus.CANCELLED);
            await this.eventsDAO.updateEvent(
                this.accountId, {
                    ...this.cachedEventObject,
                    ...{ eventStatus: this.formFields.eventStatus },
                    eventId: this.eventId,
                });

            this.trackCancelEventMixPanel(isEventCancelled ? 'Reinstate Event' : 'Cancel Event');
            this.eventSavedCallback();
        } catch (error) {
            alertServerError(errorText(), this.errorParsingService.extractMessage(error));
        }
    };

    @mobx.action
    private async completeEventFetch(eventDTO: IEventDetailsDTO): Promise<void> {
        // Since CKEditor works with classes, but data is stored with inline styles,
        // all CKEditor values need to be converted to the appropriate format
        const dto = {
            ...eventDTO,
            publicDescription: transpileInlineStylesToClasses(eventDTO.publicDescription),
            privateDescription: transpileInlineStylesToClasses(eventDTO.privateDescription),
            planningNotes: transpileInlineStylesToClasses(eventDTO.planningNotes),
            clientPlanningNotes: transpileInlineStylesToClasses(eventDTO.clientPlanningNotes)
        };
        this.cachedEventObject = dto;
        this.formFields = new EventFormDataVM(this.accountId, dto);
        this.formFields.setEventAttributes(this.eventAttributes);
        this.loading = false;
        await this.loadBillingStatus(dto);
    }

    private async loadBillingStatus(dto: IEventDetailsDTO): Promise<void> {
        if (!dto.orderId) {
            return;
        }
        try {
            const order = await this.salesforceOrderDAO.getOrderForEvent({
                accountId: this.accountId,
                eventId: dto.eventId,
                orderId: dto.orderId,
            });
            mobx.runInAction(() => {
                this.formFields.setBillingStatus(order?.order?.billingStatus ?? null);
            });
        } catch (error) {
            // Can't determine the lock state — fail open rather than block editing the rest of the form.
            console.error('Failed to load billing status for lock check', error);
        }
    }

    @mobx.action
    public submitForm = async (): Promise<boolean> => {
        if (!this.formFields.checkFormValidity() || this.saving) {
            return false;
        }

        this.saving = true;
        try {
            if (this.eventId) {
                const updatedEvent = await this.eventsDAO.updateEvent(
                    this.accountId, {
                        ...this.formFields.toApiDTO(),
                        eventId: this.eventId,
                    });

                this.trackEventMixPanel(TRACKING_EVENTS.EDIT_EVENT, this.formFields.toApiDTO());
                this.handleSalesforceSyncResult(updatedEvent);
                this.eventSavedCallback();
            } else {
                const response = await this.eventsDAO.createEvent(this.accountId, this.formFields.toApiDTO());
                this.trackEventMixPanel(TRACKING_EVENTS.NEW_EVENT, this.formFields.toApiDTO());

                this.eventSavedCallback(response.eventId);
            }

            if (this.shouldNotifyAboutEventConfiguration()) {
                this.notifyOnEventConfigurationStarted();
            }
            return true;
        } catch (e) {
            const response = JSON.parse(e.rawResponse);
            if (Array.isArray(response.fields)) {
                response.fields.forEach(error => {
                    if (error.name === ERRORS.ORDER_ID_NO_UNIQUE) {
                        mobx.runInAction(() => {
                            this.formFields.setExistingOrderIdError(error.message);
                        });
                    }
                    if (error.name === ERRORS.EXTERNAL_ID_NO_UNIQUE) {
                        mobx.runInAction(() => {
                            this.formFields.setExistingExternalIdError(error.message);
                        });
                    }
                });
            } else {
                const errorMessage = this.errorParsingService.extractMessage(e);
                alertServerError(errorText(), errorMessage);
            }
            return false;
        } finally {
            mobx.runInAction(() => {
                this.saving = false;
            });
        }
    };

    private handleSalesforceSyncResult(updatedEvent: IEventDetailsDTO): void {
        try {
            if (updatedEvent?.salesforceSynced === true) {
                this.trackSalesforceOrderEdited(updatedEvent);
                if (updatedEvent.warningMessage) {
                    alertServerWarning(
                        this.intl.formatMessage(ORDER_SAVE_WARNING_TITLE),
                        updatedEvent.warningMessage,
                    );
                }
                return;
            }
            if (updatedEvent?.salesforceSynced === false) {
                alertServerError(
                    this.intl.formatMessage(SALESFORCE_SYNC_FAILED_TITLE),
                    updatedEvent.salesforceSyncWarning,
                );
            }
        } catch (e) {
            console.error('Failed to report the Salesforce Order sync result', e);
        }
    }

    private trackSalesforceOrderEdited(updatedEvent: IEventDetailsDTO): void {
        this.trackingService.get().track(SALESFORCE_ORDER_TRACKING_EVENTS.EDIT_SALESFORCE_ORDER, {
            'Account Name': this.globalStorage.getEvent()?.accountName ?? '',
            'Account ID': this.accountId != null ? String(this.accountId) : '',
            'Event ID': this.eventId != null ? String(this.eventId) : '',
            'Order ID': updatedEvent.orderId ?? '',
            ...SalesforceSharedFieldsTrackingMapper.toTrackingProperties(updatedEvent),
        });
    }

    public trackEventMixPanel = (eventName: string, eventData: Omit<ICreateEventDTO, 'coordinators'>): void => {
        const coordinators = convertBoolTypeForMixPanel(eventData.addedCoordinators.length > 0 || eventData.removedCoordinators.length > 0);
        const deliveryTeam = convertBoolTypeForMixPanel(
                eventData.addedDeliveryTeamMembers.length > 0
                || eventData.removedDeliveryTeamMembers.length > 0
                || eventData.updatedDeliveryTeamMembers.length > 0
            );

        const publicDescription = eventName === TRACKING_EVENTS.EDIT_EVENT
            ? convertBoolTypeForMixPanel(this.cachedEventObject.publicDescription !== eventData.publicDescription)
            : convertBoolTypeForMixPanel(!!eventData.publicDescription);
        const privateDescription = eventName === TRACKING_EVENTS.EDIT_EVENT
            ? convertBoolTypeForMixPanel(this.cachedEventObject.privateDescription !== eventData.privateDescription)
            : convertBoolTypeForMixPanel(!!eventData.privateDescription);

        const clientPlanningNotes = eventName === TRACKING_EVENTS.EDIT_EVENT
            ? convertBoolTypeForMixPanel(this.cachedEventObject.clientPlanningNotes !== eventData.clientPlanningNotes)
            : convertBoolTypeForMixPanel(!!eventData.clientPlanningNotes);

        const trackingData = {
            'Account Name': this.globalStorage.getEvent()?.accountName,
            'Start date': String(this.formFields.startDateTime),
            'End date': String(this.formFields.endDateTime),
            'Timezone': eventData.timeZone,
            'Region': eventData.region || '',
            'Business Line': eventData.businessLine || '',
            'Event type': eventData.type || '',
            'Event Access': eventData.eventAccess,
            'Co-ordinators': coordinators,
            'Delivery Team': deliveryTeam,
            'Event Public Description': publicDescription,
            'Event Private Description': privateDescription,
            'Client Planning Notes': clientPlanningNotes,
            'Blueprint': this.blueprintFieldCheckMixPanel(),
        } as TObject<string>;

        if (eventName === TRACKING_EVENTS.EDIT_EVENT) {
            const thumbnailImageVM = this.formFields.uploadThumbnailImageS3VM;
            trackingData['Event Title for Passport'] = convertBoolTypeForMixPanel(!!eventData.eventTitleForPassport);
            trackingData['Event Thumbnail Present'] = convertBoolTypeForMixPanel(!!thumbnailImageVM.image.objectKey);
            trackingData['Event Thumbnail Changed'] = convertBoolTypeForMixPanel(!!thumbnailImageVM.getImageChanged);
        }

        this.trackingService.get().track(eventName, trackingData);
    };

    public trackCancelEventMixPanel = (eventName: string): void => {
        this.trackingService.get().track(eventName, {
            'Account Name': this.cachedEventObject.accountName,
            'Event ID': this.eventId
        });
    };

    private shouldNotifyAboutEventConfiguration(): boolean {
        const blueprintConfiguration = this.formFields.blueprintWithEventConfiguration() || this.formFields.blueprintWithSingleMeetingConfiguration();

        if (this.eventId) {
            return this.formFields.isBlueprintIdChanged() && blueprintConfiguration;
        }
        return blueprintConfiguration;
    }

    private notifyOnEventConfigurationStarted(): void {
        this.alertsService.alert({
            message: (
                <span className='alert-message'>
                    {this.intl.formatMessage(SETTING_UP_BLUEPRINT_ALERT_TEXT)}
                </span>
            ),
            okButtonProps: {
                text: this.intl.formatMessage(OK_TEXT).toUpperCase(),
                modifier: 'confirm-safe'
            }
        });
    }

    private trackTriggerEventDeletion(job: JobDtoType): void {
        this.jobTrackingService.trackJobTriggerResult(job);
    }

    private blueprintFieldCheckMixPanel(): string {
        const isBlueprintsAvailable = this.formFields.blueprintsAvailable;
        const isChanged = this.formFields.isBlueprintIdChanged();
        const selectedName = isBlueprintsAvailable && this.formFields.getSelectedBlueprintName();

        return `${convertBoolTypeForMixPanel(isChanged)} - ${selectedName || NONE}`;
    }

}

export { EventFormVM };
